import type { Method } from 'axios';
import axios from 'axios';

/**
 * APIs used by the Insights platform.
 */
export const enum Api {
    Auth = 'auth',
    Orgs = 'orgweb',
    Designs = 'designweb',
    Reports = 'reports',
    Cuts = 'cutweb',
    Systems = 'systemweb',
    CutState = 'cutstateweb',
    Tickets = 'tickets',
}

/**
 * Create the API URI for either local dev environment or prod environment.
 *
 * Useful due to the fact that prod gets proxies through the code deployed on
 * Cloudfront, while dev must hit the service directly.
 *
 * **Note:** Dev is not subject to caching behaviors from Cloudfront.
 */
export const createUri = (api: Api, path = '') => {
    return import.meta.env.DEV ? `https://${api}.dev.moutfittersync.net${path}` : path;
};

type ApiRequester<Input, Output> = (args: {
    body?: Input;
    token: string;
    params?: Record<string, any>;
}) => Promise<Output>;

type ApiFunction<Enum> = <Input, Output>(route: Enum, method: Method) => ApiRequester<Input, Output>;

/**
 * Composable API requests.
 */
export const createApi = <Enum>(uri: string): ApiFunction<Enum> => {
    const instance = axios.create({
        baseURL: uri,
    });

    return <Input, Output>(route: Enum, method: Method) => {
        return async ({
            body,
            token,
            params,
        }: {
            body?: Input;
            token: string;
            params?: Record<string, any>;
        }): Promise<Output> => {
            const { data } = await instance(route as string, {
                method,
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                data: body,
                params,
            });

            return data as Output;
        };
    };
};
